import React, { useEffect, useMemo, useReducer } from 'react';
import { GlobalContext } from './context';
import { GlobalActionTypes, initialState, sessionReducer } from './reducer';
import { GlobalContextData } from './types';
import { PocketVFRFormData, PocketVFRStep } from '@/types/pocket-virtual-fitting-room';
import { useQueryParams } from '@/hooks';
import { FORM_STORAGE_KEY, STEP_STORAGE_KEY } from '@/constants/pocket-virtual-fitting-room';

interface GlobalProviderProps {
  children: React.ReactNode;
}

export function GlobalProvider({ children }: GlobalProviderProps) {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const { raw: params } = useQueryParams();
  const { personaHash } = params;

  useEffect(() => {
    if (personaHash) {
      dispatch({
        kind: GlobalActionTypes.SET_PERSONA_HASH,
        payload: personaHash,
      });
    }
  }, [personaHash]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data) {
        switch (event.data.id) {
          case 'szb_cookies_update': {
            const { sid, personaHash } = event.data;

            localStorage.removeItem(FORM_STORAGE_KEY);

            dispatch({
              kind: GlobalActionTypes.RESET_DETAILS_SAVED,
              payload: null,
            });

            dispatch({
              kind: GlobalActionTypes.GET_SESSION_INFO,
              payload: {
                sessionId: sid || '',
                personaHash: personaHash || null,
              },
            });
            break;
          }
          case 'szb_error_creating_profile': {
            dispatch({
              kind: GlobalActionTypes.SET_ERROR_PROFILE,
              payload: true,
            });
            break;
          }
        }
      }
    };

    window.parent.postMessage({ id: 'szb_iframe_context_ready' }, '*');
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STEP_STORAGE_KEY) {
        const newStep = Number(e.newValue);
        if (!isNaN(newStep)) {
          setCurrentStep(newStep);
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setErrorProfile = (value: boolean | null) => {
    dispatch({
      kind: GlobalActionTypes.SET_ERROR_PROFILE,
      payload: value,
    });
  };

  const setPersonaHash = (value: string | null) => {
    dispatch({
      kind: GlobalActionTypes.SET_PERSONA_HASH,
      payload: value,
    });
  };

  const setPreviousSizeNotFound = (value: boolean) => {
    dispatch({
      kind: GlobalActionTypes.SET_PREVIOUS_SIZE_NOT_FOUND,
      payload: value,
    });
  };

  const setCurrentStep = (value: PocketVFRStep) => {
    dispatch({
      kind: GlobalActionTypes.SET_CURRENT_STEP,
      payload: value,
    });
  };

  const setLoadingRecommendation = (value: boolean) => {
    dispatch({
      kind: GlobalActionTypes.SET_LOADING_RECOMMENDATION,
      payload: value,
    });
  };

  const setDetailsSaved = (value: PocketVFRFormData) => {
    dispatch({
      kind: GlobalActionTypes.SET_DETAILS_SAVED,
      payload: value,
    });
  };

  const resetDetailsSaved = () => {
    dispatch({
      kind: GlobalActionTypes.RESET_DETAILS_SAVED,
      payload: null,
    });
  };

  const contextValue = useMemo(
    () =>
      ({
        state,
        actions: {
          setCurrentStep,
          setErrorProfile,
          setLoadingRecommendation,
          setPersonaHash,
          setPreviousSizeNotFound,
          setDetailsSaved,
          resetDetailsSaved,
        },
      }) as GlobalContextData,
    [state]
  );

  return <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>;
}
